// @flow
import React, { Fragment } from 'react';
import { get } from 'lodash';
import classnames from 'classnames';
import { messageFormat as message } from '@clubmed/utils';

import * as routeBuilder from 'Routes/routeBuilder';
import signoutAction from 'Store/user-info/actions/signout';
import { clearAllCacheFront } from 'Utils/graphql-client/cacheFront';
import * as pleUtils from 'Utils/ple-utils';
import { ICONS } from 'Components/utils/icon/icons';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

function formatWelcomeMessage(template, firstName, lastName) {
  return message(template).format({
    firstname: firstName || '',
    lastname: lastName || '',
  });
}

type Props = {
  cmsContent: CMSContent,
  userInfo: UserInfoSummary,
};

type Item = {
  icon: IconName,
  isExternalLink?: boolean,
  label: string,
  onClick?: () => void,
  target?: string,
  url: ?string,
};

export function LoggedNav({ cmsContent, userInfo }: Props) {
  const cmsNavigationSections = get(cmsContent, 'navigationSections', {});
  const trackingData = {
    actionGA: 'header_memberSpace',
    categoryGA: 'headerMenu',
  };
  const items = [
    {
      label: cmsNavigationSections.dashboard,
      url: routeBuilder.urlPattern('account'),
      icon: ICONS.NOTEBOOK,
      trackingData: {
        ...trackingData,
        labelGA: 'memberSpace_dashboard',
      },
    },
    {
      label: cmsNavigationSections.myTrips,
      url: routeBuilder.urlPattern('futureTrips'),
      icon: ICONS.DETAILS,
      trackingData: {
        ...trackingData,
        labelGA: 'memberSpace_myTrips',
      },
    },
    {
      label: cmsNavigationSections.myProfile,
      url: routeBuilder.urlPattern('profile'),
      icon: ICONS.USER,
      trackingData: {
        ...trackingData,
        labelGA: 'memberSpace_myProfile',
      },
    },
    {
      label: cmsNavigationSections.greatMembers,
      url: routeBuilder.urlPattern('greatMembers'),
      icon: ICONS.BAR,
      trackingData: {
        ...trackingData,
        labelGA: 'memberSpace_greatMembersProgram',
      },
    },
    {
      label: get(cmsContent, 'account.authentication.signout', ''),
      url: pleUtils.buildSignoutUrl(),
      icon: ICONS.DISCONNECTION,
      onClick: () => {
        clearAllCacheFront();
        signoutAction();
      },
      isExternalLink: true,
      trackingData: {
        ...trackingData,
        labelGA: 'memberSpace_signOut',
      },
    },
  ];

  const itemClasses = 'flex items-center font-bold leading-tight text-gray-darker text-md p-3';

  return (
    <Fragment>
      <div className={itemClasses}>
        {formatWelcomeMessage(
          cmsContent.welcomeLabelTemplate,
          userInfo.firstName,
          userInfo.lastName,
        )}
      </div>
      <nav>
        {items.map(({ icon, isExternalLink, label, onClick, target, url }: Item) => (
          <TrackClick
            as={DynamicLink}
            className={classnames(
              itemClasses,
              'border-t-1 focus:text-blue hover:text-blue transition-color',
            )}
            isExternalLink={isExternalLink}
            key={url}
            onClick={onClick}
            target={target}
            trackingData={trackingData}
            url={url}
          >
            {icon && <Icon className="rtl:mr-3" name={icon} width="1.25rem" />}
            {label}
          </TrackClick>
        ))}
      </nav>
    </Fragment>
  );
}
